import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';

import { ProductService } from '../product/shared/product.service';
import { ProductItem } from '../shared/models/product';

export interface CatalogResolvedData {
    products: ProductItem[];
    productConsoleVariants: Record<string, any>;
}

export const catalogResolver: ResolveFn<CatalogResolvedData> = async () => {
    const productService = inject(ProductService);

    try {
        const products = await productService.getProductList();
        const allRegions = await productService.getAllProductRegions();
        const productConsoleVariants: Record<string, any> = {};

        const productRequests = products.map(async (product) => {
            const defaultConsole =
                product.consoles.find(
                    (console) =>
                        console['console-name']?.toLowerCase() === 'led',
                ) || product.consoles[0];

            const [pdbData, consoleData] = await Promise.all([
                defaultConsole
                    ? productService.getProductPdbData(
                          product['pdb-id'],
                          +defaultConsole['console-pdb-id'] || undefined,
                      )
                    : productService.getProductPdbData(product['pdb-id']),
                productService.getAllConsolePdbData(product.consoles),
            ]);

            for (const console of product.consoles) {
                const key = `${product['pdb-id']}_${console['console-pdb-id']}`;
                productConsoleVariants[key] =
                    await productService.getProductPdbData(
                        product['pdb-id'],
                        +console['console-pdb-id'] || undefined,
                    );
            }

            const mappedConsoles = product.consoles.map((console) => ({
                ...console,
                ...consoleData.find(
                    (data) => data && +console['console-pdb-id'] === +data.id,
                ),
            }));

            if (product.consoles?.length) {
                product.consoleNamesForFilter = product.consoles.map(
                    (c) => c['console-name'],
                );
                product.consoleNamesForFilter.push('all-consoles');
            } else {
                product.consoleNamesForFilter = ['no-consoles'];
            }

            if (product.path) {
                let pathForFilter = product.path?.split('/')[1] || '';
                if (pathForFilter) {
                    product.pathForFilter = pathForFilter.replace(/^./, (c) =>
                        c.toUpperCase(),
                    );
                }
            }

            if (
                product.regions &&
                product.regions.some(
                    (region) =>
                        region['country-name'] === 'All' ||
                        region['region-name'] === 'All',
                )
            ) {
                product.regions = allRegions;
            }

            const combinedProduct = {
                ...product,
                ...pdbData,
                consoles: mappedConsoles,
                selectedConsole: defaultConsole,
            } as ProductItem;

            const translatedWebDisplayName =
                combinedProduct.productMarketingInfo?.find(
                    (info) => info.infoType === 'Web Display Name',
                );
            combinedProduct.webDisplayName = translatedWebDisplayName?.value
                ? translatedWebDisplayName.value
                : combinedProduct.webDisplayName;

            return combinedProduct;
        });

        const combinedProducts: ProductItem[] = await Promise.all(
            productRequests,
        );

        return {
            products: combinedProducts,
            productConsoleVariants,
        };
    } catch (error) {
        console.error('Error resolving catalog data:', error);
        return {
            products: [],
            productConsoleVariants: {},
        };
    }
};
